import { Modal, ModalFooter, ModalHeader } from 'components/Modal'
import * as React from 'react'
import { KeyboardEvent } from 'react'
import { DisplayContent } from 'react-content-engine'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'

import ButtonExtended from 'components/ButtonExtended'
import { AutoSuggest } from 'components/Select'
import Style from 'components/Style'
import { Tag } from 'components/Tags'
import { Tags } from 'components/Tags/Tags'
import { City } from '../../../data/models/funnelOrder/cities'
import { destinationEnum } from '../../../data/models/funnelOrder/destination'
import {
  getExcludedCitiesContentKey,
  getMaxExcludedCities,
} from '../../../data/models/funnelOrder/excludedCities'
import { Theme, ThemeTypes } from '../../../data/models/funnelOrder/theme'
import { getSiteCountryICC2, SiteCountry } from '../../../data/models/locale'
import { getClient } from '../../../data/openapi'
import { AppState } from '../../../data/state'
import AutoCityExcludes from '../../../elements/excluded/AutoCityExcludes'
import {
  placeholder,
  searchPromptText,
} from '../../../elements/excluded/options'
import { getCoronaLink } from '../../../urls/corona'
import Content from '../../shared/content'

interface ExcludeCitiesProps {
  country: SiteCountry
  theme: Theme
  excludedCities: City[]
  destination: destinationEnum
  setExcludedCities(value: City[]): void
}

export interface ExcludeCitiesOptions {
  value: number
  label: string
  direction?: string
}

interface ExcludeCitiesState {
  showExcludeCities: boolean
  excludedCities: City[]
  themeCities: ExcludeCitiesOptions[]
}

class ExcludeCities extends React.Component<
  ExcludeCitiesProps,
  ExcludeCitiesState
> {
  constructor(props: ExcludeCitiesProps) {
    super(props)

    this.state = {
      showExcludeCities: false,
      excludedCities: props.excludedCities,
      themeCities: [],
    }
    this.addTag = this.addTag.bind(this)
  }

  public componentDidMount() {
    const data = {
      theme: 'all',
      type:
        this.props.theme === Theme.train ? ThemeTypes.train : ThemeTypes.road,
      site_country: getSiteCountryICC2(this.props.country),
    }
    getClient()
      .then(client => client.apis.bookings.bookings_theme_cities(data))
      .then(response => response.body)
      .then(cities => {
        this.setState({
          themeCities: cities.map((x: any) => ({
            value: x.id,
            label: x.name,
            direction: x.direction,
          })),
        })
      })
  }

  public handleSave = () => {
    this.addTag().then(() => {
      this.props.setExcludedCities(this.state.excludedCities)
      this.toggleExcludeCities()
    })
  }

  public toggleExcludeCities = () => {
    this.setState({
      showExcludeCities: !this.state.showExcludeCities,
    })
  }

  public onKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      this.addTag()
    }
  }

  public addTag = () => {
    return new Promise<void>((resolve, reject) => {
      if (this.state.excludedCities.length) {
        const max = getMaxExcludedCities(
          this.props.theme,
          this.props.destination,
        )
        const excludedCities = [...this.state.excludedCities].slice(0, max)

        this.setState(
          {
            excludedCities,
          },
          resolve,
        )
      }
      resolve()
    })
  }

  public onDeleteTag = (id: number) => {
    const excludedCities = this.props.excludedCities.filter(
      city => city.id !== id,
    )
    this.setState({ excludedCities })
  }

  public render() {
    const { theme, destination, country } = this.props
    const { excludedCities } = this.state

    const subHeadingContentKey = getExcludedCitiesContentKey(
      theme,
      destination,
      'exlude-cities-subheading',
    )
    const max = getMaxExcludedCities(theme, destination)
    const contentContentKey = getExcludedCitiesContentKey(theme, destination)

    const contentKeys = {
      content: contentContentKey,
      subContent: 'corona-virus-content',
    }

    return (
      <>
        <ButtonExtended
          i={this.state.excludedCities.length}
          iMax={max}
          isActive={this.state.excludedCities.length > 0}
          header={
            <Content contentKey="exlude-cities-heading">
              Exclude cities?
            </Content>
          }
          subHeader={
            <Content contentKey={subHeadingContentKey}>
              {`Feel free to exclude two cities ${subHeadingContentKey}`}
            </Content>
          }
          onClick={this.toggleExcludeCities}
        />
        {this.state.showExcludeCities ? (
          <Modal
            size="xl"
            sticky={true}
            contentOverflow
            onDismiss={() => {
              this.toggleExcludeCities()
              this.setState({ excludedCities: [] })
            }}
            modalName={`excludeCitiesModal`}
          >
            <ModalHeader backButton={true}>
              <Content contentKey="exclude-cities-title">
                Exclude cities
              </Content>
            </ModalHeader>

            <div className="content">
              <p>
                <DisplayContent contentKey={contentKeys.content}>
                  Exclude cities
                </DisplayContent>
              </p>

              <p>
                <a href={getCoronaLink(country)} target="_blank">
                  <DisplayContent contentKey={contentKeys.subContent}>
                    {`Exclude cities subcontent`}
                  </DisplayContent>
                </a>
              </p>

              {excludedCities.length >= max ? null : (
                <Style margin={[[0, 0, 20, 0]]}>
                  <AutoSuggest
                    options={this.getCities()}
                    onChange={this.addTagToExluded}
                    placeholder={placeholder[country]}
                    searchPromptText={searchPromptText[country]}
                  />
                </Style>
              )}
              <Tags>
                <AutoCityExcludes />
                {excludedCities.slice(0, max).map((value, i) => (
                  <Tag
                    key={value.id}
                    text={value.name}
                    onDelete={() => this.onDeleteTag(value.id)}
                  />
                ))}
              </Tags>
            </div>
            <ModalFooter
              hideClose={true}
              onClose={this.toggleExcludeCities}
              onSave={this.handleSave}
            />
          </Modal>
        ) : (
          ''
        )}
      </>
    )
  }

  private getMax = () => {
    return getMaxExcludedCities(this.props.theme, this.props.destination)
  }

  private addTagToExluded = (value: ExcludeCitiesOptions) => {
    if (value !== undefined) {
      const max = this.getMax()
      const cityValue: City = {
        id: value.value,
        name: value.label,
        direction: value.direction,
      }
      const excludedCities = [...this.state.excludedCities, cityValue].slice(
        0,
        max,
      )

      this.setState({ excludedCities })
    }
  }

  private getCities = () => {
    return Promise.resolve(this.state.themeCities)
  }
}
export default connect(
  (state: AppState) => ({
    theme: state.funnelOrder.theme,
    excludedCities: state.funnelOrder.excludedCitiesIds,
    country: state.locale.country,
  }),
  dispatch => ({
    setExcludedCities: (value: City[]) => {
      dispatch(actions.change('funnelOrder.excludedCitiesIds', value))
    },
  }),
)(ExcludeCities)
